import { useEffect, useRef, useState } from 'react';

const pains = [
  {
    num: '01',
    title: 'RevPAR Growth is Stalling While Costs Keep Climbing',
    desc: 'Labor costs are 15–20% above pre-pandemic levels, ADR pressure is mounting, and profit margins are shrinking even as occupancy holds. You need a professional Hotel Revenue Management Service — not guesswork.',
    icon: 'ri-arrow-up-down-line',
  },
  {
    num: '02',
    title: 'Your Management Agreement Isn\'t Working For You',
    desc: 'Most hotel management contracts are written for operators, not owners. With $20–25B in CMBS maturities hitting 2025–2026, it\'s time to renegotiate — or find a partner who will.',
    icon: 'ri-file-damage-line',
  },
  {
    num: '03',
    title: 'Your Asset Isn\'t Hitting Its NOI Potential',
    desc: 'Cap rate compression means every dollar of NOI improvement translates directly to asset value. Without a dedicated Hotel Revenue Management Service, you\'re leaving valuation gains on the table.',
    icon: 'ri-building-line',
  },
  {
    num: '04',
    title: 'You\'re Managing Operations — Not Growing a Business',
    desc: '35–40% of independent hotels lack a formal third-party operator. If your time is consumed by day-to-day ops, you\'re not focused on the strategic decisions that build value — and you need a full-service Hotel Management Service to free you up.',
    icon: 'ri-time-line',
  },
  {
    num: '05',
    title: 'Big Firms Don\'t Move Fast Enough for Boutique Properties',
    desc: 'National hotel management companies target institutional owners and branded portfolios. Independent and boutique hotel owners are completely underserved by traditional Hotel Brand Management Services — and they know it.',
    icon: 'ri-building-4-line',
  },
  {
    num: '06',
    title: 'You Need Accountability, Not Just a Management Fee',
    desc: 'Most operators charge base fees regardless of performance. We believe your Hotel Management Service partner should have skin in the game — because that\'s what a real partner does.',
    icon: 'ri-hand-coin-line',
  },
];

export default function PainPoints() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <p className="text-xs font-light text-amber-600 uppercase tracking-widest mb-4" style={{ letterSpacing: '0.25em' }}>
            Sound Familiar?
          </p>
          <h2
            className="text-4xl lg:text-5xl font-extralight text-gray-900 leading-tight"
          >
            The Challenges Every Independent Hotel Owner Faces
          </h2>
        </div>

        {/* Cards Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pains.map((pain, i) => (
            <div
              key={i}
              className={`group relative p-8 rounded-2xl border border-gray-100 hover:border-amber-200 bg-white hover:bg-amber-50/30 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Number */}
              <span
                className="absolute top-6 right-8 text-5xl font-extralight text-gray-100 group-hover:text-amber-100 transition-colors select-none"
              >
                {pain.num}
              </span>

              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center mb-5">
                <i className={`${pain.icon} text-2xl text-amber-600`}></i>
              </div>

              {/* Content */}
              <h3 className="text-base font-normal text-gray-900 mb-3 leading-snug pr-8">
                {pain.title}
              </h3>
              <p className="text-sm font-light text-gray-500 leading-relaxed">
                {pain.desc}
              </p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-8 right-8 h-px bg-amber-200 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
